import React, { useState, useEffect } from 'react';
import { Calendar, User, Mail, Phone, DollarSign, Eye, Package, MapPin, Users as UsersIcon, Trash2 } from 'lucide-react';
import { getAllBookings, deleteBooking } from '../../../services/bookings';
import LoadingSpinner from '../../shared/LoadingSpinner';
import './BookingsList.css';

const BookingsList = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    setLoading(true);
    const result = await getAllBookings();
    console.log('📦 Bookings loaded:', result);
    if (result.success) {
      setBookings(result.data);
    } else {
      console.error('Error loading bookings:', result.error);
    }
    setLoading(false);
  };

  const handleDelete = async (bookingId, e) => {
    e.stopPropagation(); // Prevent opening the modal
    
    if (!window.confirm('Are you sure you want to delete this booking?')) {
      return;
    }

    const result = await deleteBooking(bookingId);
    
    if (result.success) {
      loadBookings(); // Reload the list
    } else {
      alert('Error deleting booking: ' + result.error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getItemIcon = (type) => {
    switch(type) {
      case 'location': return <MapPin size={16} />;
      case 'equipment': return <Package size={16} />;
      case 'talent': return <UsersIcon size={16} />;
      default: return <Package size={16} />;
    }
  };

  const filteredBookings = bookings.filter((booking) => {
    if (filterStatus === 'all') return true;
    return booking.status === filterStatus;
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="bookings-list">
      {/* Filters */}
      <div className="bookings-filters">
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            All ({bookings.length})
          </button>
          <button
            className={`filter-btn ${filterStatus === 'confirmed' ? 'active' : ''}`}
            onClick={() => setFilterStatus('confirmed')}
          >
            Confirmed ({bookings.filter((b) => b.status === 'confirmed').length})
          </button>
          <button
            className={`filter-btn ${filterStatus === 'completed' ? 'active' : ''}`}
            onClick={() => setFilterStatus('completed')}
          >
            Completed ({bookings.filter((b) => b.status === 'completed').length})
          </button>
        </div>
      </div>

      {/* Bookings Table */}
      {filteredBookings.length === 0 ? (
        <div className="admin-empty-state">
          <div className="admin-empty-icon">
            <Calendar size={64} />
          </div>
          <h3 className="admin-empty-title">No Bookings Yet</h3>
          <p className="admin-empty-text">
            Bookings will appear here once customers start making reservations.
          </p>
        </div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>Customer</th>
                <th>Contact</th>
                <th>Items</th>
                <th>Total</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings.map((booking) => (
                <tr key={booking.id}>
                  <td>
                    <span className="booking-id-cell">
                      {booking.id.substring(0, 8)}...
                    </span>
                  </td>
                  <td>
                    <div className="customer-cell">
                      <User size={16} />
                      <span>{booking.customerName || 'N/A'}</span>
                    </div>
                  </td>
                  <td>
                    <div className="contact-cell">
                      <div className="contact-item">
                        <Mail size={14} />
                        <span>{booking.customerEmail || 'N/A'}</span>
                      </div>
                      {booking.customerPhone && (
                        <div className="contact-item">
                          <Phone size={14} />
                          <span>{booking.customerPhone}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    {booking.items ? (
                      <span>{booking.items.length} item{booking.items.length !== 1 ? 's' : ''}</span>
                    ) : (
                      <span>0 items</span>
                    )}
                  </td>
                  <td className="table-price">
                    ${booking.totalAmount ? booking.totalAmount.toFixed(2) : '0.00'}
                  </td>
                  <td>{formatDate(booking.createdAt)}</td>
                  <td>
                    <span className={`status-badge ${booking.status || 'confirmed'}`}>
                      {booking.status || 'confirmed'}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        onClick={() => setSelectedBooking(booking)}
                        className="btn btn-ghost btn-sm"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={(e) => handleDelete(booking.id, e)}
                        className="btn btn-ghost btn-sm btn-danger"
                        title="Delete Booking"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <div className="modal-overlay" onClick={() => setSelectedBooking(null)}>
          <div className="booking-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Booking Details</h2>
              <button
                onClick={() => setSelectedBooking(null)}
                className="modal-close"
              >
                ×
              </button>
            </div>

            <div className="booking-modal-content">
              {/* Customer Information */}
              <div className="booking-section">
                <h3 className="booking-section-title">Customer Information</h3>
                <div className="booking-info-grid">
                  <div className="info-item">
                    <User size={18} />
                    <div>
                      <div className="info-label">Name</div>
                      <div className="info-value">{selectedBooking.customerName || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="info-item">
                    <Mail size={18} />
                    <div>
                      <div className="info-label">Email</div>
                      <div className="info-value">{selectedBooking.customerEmail || 'N/A'}</div>
                    </div>
                  </div>
                  {selectedBooking.customerPhone && (
                    <div className="info-item">
                      <Phone size={18} />
                      <div>
                        <div className="info-label">Phone</div>
                        <div className="info-value">{selectedBooking.customerPhone}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Booked Items */}
              <div className="booking-section">
                <h3 className="booking-section-title">Booked Items</h3>
                {selectedBooking.items && selectedBooking.items.length > 0 ? (
                  <div className="booked-items-list">
                    {selectedBooking.items.map((item, index) => (
                      <div key={index} className="booked-item-card">
                        <div className="booked-item-header">
                          <div className="item-type-badge">
                            {getItemIcon(item.type)}
                            <span>{item.type}</span>
                          </div>
                          <span className="item-price">${item.total ? item.total.toFixed(2) : '0.00'}</span>
                        </div>
                        <div className="item-name">{item.name || 'Unnamed Item'}</div>
                        <div className="item-dates">
                          <Calendar size={14} />
                          <span>
                            {formatDate(item.startDate)} - {formatDate(item.endDate)}
                          </span>
                          <span className="item-days">({item.days || 0} days)</span>
                        </div>
                        <div className="item-price-breakdown">
                          ${item.price || 0}/day × {item.days || 0} days = ${item.total ? item.total.toFixed(2) : '0.00'}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: 'var(--color-text-muted)' }}>No items found</p>
                )}
              </div>

              {/* Notes */}
              {selectedBooking.notes && (
                <div className="booking-section">
                  <h3 className="booking-section-title">Notes</h3>
                  <div className="notes-box">{selectedBooking.notes}</div>
                </div>
              )}

              {/* Payment */}
              <div className="booking-section">
                <h3 className="booking-section-title">Payment</h3>
                <div className="payment-info">
                  <div className="payment-row">
                    <span>Total Amount:</span>
                    <span className="payment-amount">
                      ${selectedBooking.totalAmount ? selectedBooking.totalAmount.toFixed(2) : '0.00'}
                    </span>
                  </div>
                  <div className="payment-row">
                    <span>Payment Status:</span>
                    <span className="status-badge confirmed">
                      {selectedBooking.paymentStatus || 'completed'}
                    </span>
                  </div>
                  <div className="payment-row">
                    <span>Booking Status:</span>
                    <span className={`status-badge ${selectedBooking.status || 'confirmed'}`}>
                      {selectedBooking.status || 'confirmed'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Booking Info */}
              <div className="booking-section" style={{ borderBottom: 'none' }}>
                <h3 className="booking-section-title">Booking Information</h3>
                <div className="payment-info">
                  <div className="payment-row">
                    <span>Booking ID:</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{selectedBooking.id}</span>
                  </div>
                  <div className="payment-row">
                    <span>Created:</span>
                    <span>{formatDate(selectedBooking.createdAt)}</span>
                  </div>
                  {selectedBooking.updatedAt && (
                    <div className="payment-row">
                      <span>Last Updated:</span>
                      <span>{formatDate(selectedBooking.updatedAt)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingsList;